import { useMutation } from '@tanstack/react-query';
import { getTimezone, hashPassword } from '@mgm/common/utils';
import { usePostHog } from 'posthog-js/react';
import { OnboardingController } from '../../services';
import { TRegisterRequest } from '../../types/requests';
import { IBaseResponse, TAxiosError } from '../../types/responses';
import { useLoginAPI } from './useLogin';
import { useError } from '../useCommon';
import { useAppSelector } from '../useAppStore';
import { getAuthenticateUser } from '../../store/authenticate/selectors';

export function useRegister() {
  const { mutateAsync: loginMutate } = useLoginAPI();
  const { handleReqError } = useError();
  const posthog = usePostHog();

  const registerApi = (newUser: TRegisterRequest) => {
    const data = {
      shopName: newUser.shopName || '',
      email: newUser.email?.toLowerCase(),
      password: hashPassword(newUser.password),
      userName: newUser.userName,
      timezone: getTimezone(),
    };

    return OnboardingController().register<IBaseResponse>(data);
  };

  return useMutation(registerApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success) {
        const loginData = {
          email: variables.email ?? '',
          password: variables.password ?? '',
          isRedirect: false,
          isDisplayToast: false,
          isRegistered: true,
        };

        posthog.capture('ManageMate_web', {
          shopName: variables?.shopName,
          newMember: variables?.email,
          addedAt: new Date().toISOString(),
          action: 'new_user_registered',
        });

        await loginMutate(loginData);
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error);
      } else if ('msg' in rsp) {
        console.error(`ERROR! onboarding register failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! onboarding register request threw an Exception! ${error}`);
    },
  });
}

type TPricingPlanApi = {
  planName: 'free' | 'lite' | 'standard' | 'enterprise',
  shopName: string
  planChange: boolean | null
};

export function useSetPricingPlanAPI() {
  const { handleReqError } = useError();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const posthog = usePostHog();

  const setPricingPlanApi = (options: TPricingPlanApi) => {
    const data = {
      shopName: options.shopName || personnel?.shopName,
      planName: options.planName,
      ...(options.planChange && {
        planChange: true,
      }),
    };

    return OnboardingController().setPricingPlan<IBaseResponse & { redirectUrl: string }>(data);
  };

  return useMutation(setPricingPlanApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp?.success && 'redirectUrl' in rsp) {
        posthog.capture('ManageMate_web', {
          shopName: variables?.shopName || '',
          addedAt: new Date().toISOString(),
          action: 'new_user_registered',
        });

        window.open(rsp.redirectUrl);
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error);
      } else if ('msg' in rsp) {
        console.error(`ERROR! set pricing plan failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! set pricing plan request threw an Exception! ${error}`);
    },
  });
}
